import { CoreError, CoreErrorCause } from '@domain/models/core-error'

function getErrorCode(error: Error): string | null {
  if ('code' in error && typeof error.code === 'string') {
    return error.code
  }
  return null
}

export class AuthErrorConverter {
  public static domainModel(fromExternalModel: unknown): CoreError {
    if (fromExternalModel instanceof CoreError) {
      return fromExternalModel
    }

    if (!(fromExternalModel instanceof Error)) {
      return new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `An Error that is not Firebase Auth error happened: ${String(fromExternalModel)}`,
        underlyingException: fromExternalModel,
      })
    }

    const code = getErrorCode(fromExternalModel)

    switch (code) {
      case 'auth/invalid-email':
        return new CoreError({
          cause: CoreErrorCause.InvalidCredential,
          context: 'The email address is badly formatted.',
          underlyingException: fromExternalModel,
        })
      case 'auth/user-disabled':
        return new CoreError({
          cause: CoreErrorCause.Forbidden,
          context: 'The user corresponding to the given email has been disabled.',
          underlyingException: fromExternalModel,
        })
      case 'auth/user-not-found':
        return new CoreError({
          cause: CoreErrorCause.DataNotFound,
          context: 'There is no user corresponding to the given email.',
          underlyingException: fromExternalModel,
        })
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return new CoreError({
          cause: CoreErrorCause.InvalidCredential,
          context: 'The password is invalid or the account does not have a password set.',
          underlyingException: fromExternalModel,
        })
      case 'auth/email-already-in-use':
        return new CoreError({
          cause: CoreErrorCause.EmailAlreadyInUse,
          context: 'The email address is already in use by another account.',
          underlyingException: fromExternalModel,
        })
      case 'auth/operation-not-allowed':
        return new CoreError({
          cause: CoreErrorCause.Forbidden,
          context: 'This operation is not allowed. Please contact support.',
          underlyingException: fromExternalModel,
        })
      case 'auth/weak-password':
        return new CoreError({
          cause: CoreErrorCause.WeakPassword,
          context: 'The password provided is too weak.',
          underlyingException: fromExternalModel,
        })
      case 'auth/too-many-requests':
        return new CoreError({
          cause: CoreErrorCause.Forbidden,
          context: 'Too many requests. Please try again later.',
          underlyingException: fromExternalModel,
        })
      case 'auth/requires-recent-login':
        return new CoreError({
          cause: CoreErrorCause.RequireRecentAuthentication,
          context: 'This operation requires recent authentication. Please log in again.',
          underlyingException: fromExternalModel,
        })
      case 'auth/network-request-failed':
        return new CoreError({
          cause: CoreErrorCause.NetworkConnection,
          context: 'A network error occurred. Please check your connection and try again.',
          underlyingException: fromExternalModel,
        })
      default:
        return new CoreError({
          cause: CoreErrorCause.Unknown,
          context: `An undefined Error happened: ${fromExternalModel.message}`,
          underlyingException: fromExternalModel,
        })
    }
  }
}
